import { useState, useEffect, useCallback } from 'react';
import type { User } from '@auth0/auth0-react';
import { useAuth0 } from '@auth0/auth0-react';
import { auth0 as auth0EnvConfig } from '@/shared/config';
import type { UserRole } from '@/shared/types';
import { logger } from '@/shared/utils/logger';
import { extractRoleFromClaims } from './auth.utils';

interface Auth0Result {
  accessToken: string | null;
  initialRole: UserRole | null;
  auth0User: User | undefined;
  auth0IsAuthenticated: boolean;
  auth0Loading: boolean;
  auth0Error: Error | null | undefined;
  login: (
    context?: 'client' | 'provider' | 'staff',
    inviteCode?: string,
  ) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  getNewToken: (options?: GetNewTokenOptions) => Promise<string | null>;
}

export interface GetNewTokenOptions {
  forceRefresh?: boolean;
}

const EXPECTED_AUTH0_SESSION_ERROR_CODES = new Set([
  'invalid_grant',
  'login_required',
  'timeout',
]);

const EXPECTED_AUTH0_SESSION_ERROR_MESSAGES = new Set([
  'invalid grant',
  'login required',
  'timeout',
]);

const isExpectedAuth0SessionError = (error: unknown): boolean => {
  if (!error) return false;

  if (typeof error === 'object') {
    const authError = error as { error?: unknown; message?: unknown };

    if (
      typeof authError.error === 'string' &&
      EXPECTED_AUTH0_SESSION_ERROR_CODES.has(authError.error)
    ) {
      return true;
    }

    if (
      typeof authError.message === 'string' &&
      EXPECTED_AUTH0_SESSION_ERROR_MESSAGES.has(authError.message.toLowerCase())
    ) {
      return true;
    }
  }

  return typeof error === 'string' &&
    EXPECTED_AUTH0_SESSION_ERROR_MESSAGES.has(error.toLowerCase());
};

const EXPECTED_AUTH0_INTERACTIVE_ERROR_CODES = new Set([
  'consent_required',
  'interaction_required',
]);

const EXPECTED_AUTH0_INTERACTIVE_ERROR_MESSAGES = new Set([
  'consent required',
  'interaction required',
]);

const isExpectedAuth0InteractiveError = (error: unknown): boolean => {
  if (!error) return false;

  if (typeof error === 'object') {
    const authError = error as { error?: unknown; message?: unknown };

    if (
      typeof authError.error === 'string' &&
      EXPECTED_AUTH0_INTERACTIVE_ERROR_CODES.has(authError.error)
    ) {
      return true;
    }

    if (
      typeof authError.message === 'string' &&
      EXPECTED_AUTH0_INTERACTIVE_ERROR_MESSAGES.has(authError.message.toLowerCase())
    ) {
      return true;
    }
  }

  return typeof error === 'string' &&
    EXPECTED_AUTH0_INTERACTIVE_ERROR_MESSAGES.has(error.toLowerCase());
};

/**
 * Hook to handle direct Auth0 integration
 *
 * This separates Auth0-specific concerns from application auth logic.
 * User provisioning now happens via JIT (Just-In-Time) on the backend,
 * so no need to sync users from the frontend.
 */
export const useAuth0Integration = (): Auth0Result => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [initialRole, setInitialRole] = useState<UserRole | null>(null);
  const [isTokenFetching, setIsTokenFetching] = useState(false);
  const [hasAuthTimedOut, setHasAuthTimedOut] = useState(false);

  const {
    user: auth0User,
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0Loading,
    logout: auth0Logout,
    getIdTokenClaims,
    loginWithRedirect: auth0LoginWithRedirect,
    error: auth0Error,
    getAccessTokenSilently,
  } = useAuth0();

  const namespace = auth0EnvConfig.claimsNamespace;

  // Add timeout for Auth0 loading state to prevent infinite loading
  useEffect(() => {
    if (auth0Loading) {
      const timeout = setTimeout(() => {
        logger.warn(
          'Auth0 loading timeout - preserving session while recovery continues',
        );
        setHasAuthTimedOut(true);
      }, 8000); // 8 second timeout

      return () => clearTimeout(timeout);
    } else {
      // Reset timeout flag when loading completes
      setHasAuthTimedOut(false);
    }
  }, [auth0Loading]);

  // Get token claims and role when user is authenticated
  useEffect(() => {
    // Skip the effect entirely if not authenticated or still loading
    if (!auth0IsAuthenticated || auth0Loading) return;

    const getTokenAndClaims = async () => {
      try {
        // Double-check authentication before proceeding
        if (!auth0User) return;

        setIsTokenFetching(true);

        // Add timeout for token fetching
        const tokenPromise = getAccessTokenSilently({
          timeoutInSeconds: 10, // 10 second timeout
        });

        const claims = await getIdTokenClaims();
        const token = await tokenPromise;

        setAccessToken(token);
        const role = claims ? extractRoleFromClaims(claims, namespace) : null;
        setInitialRole(role);

        logger.debug('Token and claims fetched', {
          hasToken: !!token,
          role,
          userId: auth0User.sub,
        });
      } catch (error: unknown) {
        // Only log the error if we're actually authenticated
        if (auth0IsAuthenticated) {
          if (isExpectedAuth0SessionError(error)) {
            logger.warn('Auth session expired or requires re-login:', error);

            // Force logout to clear all persisted state and redirect to login
            auth0Logout({
              logoutParams: {
                returnTo: window.location.origin,
              },
            });

            // Clear local storage to ensure clean state
            localStorage.removeItem('auth-storage');
          } else {
            logger.error('Error getting token and claims:', error);
          }
        }

        // Reset state on error to ensure consistent state
        setAccessToken(null);
        setInitialRole(null);
      } finally {
        setIsTokenFetching(false);
      }
    };

    getTokenAndClaims();
  }, [
    auth0IsAuthenticated,
    auth0User,
    auth0Loading,
    getIdTokenClaims,
    getAccessTokenSilently,
    namespace,
    auth0Logout,
  ]);

  const login = useCallback(async (
    context: 'client' | 'provider' | 'staff' = 'client',
    inviteCode?: string,
  ) => {
    // Build the authorization params that will be available in Auth0 Action
    const authorizationParams: Record<string, string> = {
      // Pass context as a parameter that Auth0 will forward
      context,
    };

    if (inviteCode) {
      authorizationParams.invite = inviteCode;
    }

    await auth0LoginWithRedirect({
      appState: {
        returnTo: window.location.pathname || window.location.origin,
        ...(context === 'provider' && { providerIntent: true }),
      },
      authorizationParams,
    });
  }, [auth0LoginWithRedirect]);

  const logout = useCallback(() => {
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
    setAccessToken(null);
    setInitialRole(null);
  }, [auth0Logout]);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (!auth0IsAuthenticated || !auth0User) return false;

    try {
      setIsTokenFetching(true);

      // Force refresh the token
      const token = await getAccessTokenSilently({
        cacheMode: 'off', // Force a new token
        timeoutInSeconds: 10,
      });

      setAccessToken(token);

      // Get updated claims
      const claims = await getIdTokenClaims();
      const role = claims ? extractRoleFromClaims(claims, namespace) : null;
      setInitialRole(role);

      logger.debug('Token refreshed', { role, userId: auth0User.sub });
      return true;
    } catch (error) {
      if (isExpectedAuth0SessionError(error)) {
        logger.warn('Auth session expired while refreshing token:', error);
      } else {
        logger.error('Error refreshing token:', error);
      }
      return false;
    } finally {
      setIsTokenFetching(false);
    }
  }, [
    auth0IsAuthenticated,
    auth0User,
    getAccessTokenSilently,
    getIdTokenClaims,
    namespace,
  ]);

  /**
   * Get a new access token - returns the token string directly
   * Used by axios interceptor for 401 retry logic
   */
  const getNewToken = useCallback(async (
    options: GetNewTokenOptions = {},
  ): Promise<string | null> => {
    const { forceRefresh = true } = options;

    try {
      const token = await getAccessTokenSilently(
        forceRefresh
          ? {
              cacheMode: 'off', // Force a new token
              timeoutInSeconds: 10,
            }
          : {
              timeoutInSeconds: 10,
            },
      );

      setAccessToken(token);

      logger.debug('New token obtained', { userId: auth0User?.sub });
      return token;
    } catch (error) {
      if (isExpectedAuth0SessionError(error)) {
        logger.warn('Auth session expired while getting new token:', error);
      } else if (isExpectedAuth0InteractiveError(error)) {
        if (forceRefresh) {
          logger.warn(
            'Auth0 requires interactive authorization while getting new token:',
            error,
          );
        } else {
          logger.debug('Silent token check requires interactive authorization:', error);
        }
      } else {
        logger.error('Error getting new token:', error);
      }
      return null;
    }
  }, [auth0User, getAccessTokenSilently]);

  return {
    accessToken,
    initialRole,
    auth0User,
    auth0IsAuthenticated,
    auth0Loading: (auth0Loading && !hasAuthTimedOut) || isTokenFetching,
    auth0Error,
    login,
    logout,
    refreshToken,
    getNewToken,
  };
};
